// ─── FuelOrderSection — campos específicos das OTs de Distribuição ──
//
// Mostra-se quando serviceType === 'fuel'. Recolhe na criação:
//   · Produto (gasóleo / gasolina / jet-A1 / outro)
//   · Capacidade esperada em m³ (pre-popular do veículo escolhido depois
//     do despacho; aqui o operador define a expectativa do pedido)
//   · Comboio: ligar a outras OTs fuel para tratamento conjunto
//     (linkedToOtIds + coordinatedDelivery)
//
// Não toca em vehicleId / cisternVehicleId — essa atribuição vive no
// dialog "Atribuir Veículo" (Sprint posterior). Aqui é só intenção.
import React from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Switch, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';

const useStyles = makeStyles(() => ({
  wrap: {
    padding: '12px 14px',
    borderRadius: 10,
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
    border: '1px solid #fed7aa',
    marginBottom: 12,
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  title: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: '0.86rem', fontWeight: 800, color: '#9a3412',
  },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 },
  row2: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8, alignItems: 'center' },
  helpText: {
    fontSize: '0.66rem', color: '#92400e', fontStyle: 'italic',
  },
}));

// O `disablePortal` do menu garante que o popover renderiza dentro do
// próprio Dialog do OrderForm (sem este, fica por baixo em alguns browsers).
const selectMenuProps = {
  disablePortal: true,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const FUEL_PRODUCTS = [
  { key: 'gasoleo',  labelPt: 'Gasóleo',           labelEn: 'Diesel' },
  { key: 'gasolina', labelPt: 'Gasolina',          labelEn: 'Gasoline' },
  { key: 'jet_a1',   labelPt: 'Jet A-1',           labelEn: 'Jet A-1' },
  { key: 'outro',    labelPt: 'Outro / múltiplo',  labelEn: 'Other / mixed' },
];

function FuelOrderSection({ fuelForm, onChange, lang = 'pt' }) {
  const classes = useStyles();
  const pt = lang === 'pt';

  const handle = (field, value) => onChange({ ...fuelForm, [field]: value });

  return (
    <Box className={classes.wrap}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          <LocalGasStationIcon style={{ fontSize: 18, color: '#ea580c' }} />
          {pt ? 'Detalhes da distribuição' : 'Distribution details'}
        </Typography>
      </Box>

      <Box className={classes.row3}>
        <FormControl size="small" variant="outlined">
          <InputLabel id="fuel-product-label">{pt ? 'Produto' : 'Product'}</InputLabel>
          <Select
            labelId="fuel-product-label"
            label={pt ? 'Produto' : 'Product'}
            value={fuelForm.fuelProduct || ''}
            onChange={e => handle('fuelProduct', e.target.value)}
            MenuProps={selectMenuProps}
          >
            <MenuItem value=""><em>— {pt ? 'Por definir' : 'Unset'} —</em></MenuItem>
            {FUEL_PRODUCTS.map(p => (
              <MenuItem key={p.key} value={p.key}>{pt ? p.labelPt : p.labelEn}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small" variant="outlined" type="number"
          label={pt ? 'Capacidade esperada (m³)' : 'Expected capacity (m³)'}
          value={fuelForm.cisternCapacityM3 ?? ''}
          onChange={e => handle('cisternCapacityM3', e.target.value)}
          inputProps={{ min: 0, max: 100, step: 0.5 }}
          helperText={pt ? 'Ex.: 25, 35, 60' : 'e.g. 25, 35, 60'}
        />

        <TextField
          size="small" variant="outlined"
          label={pt ? 'Litros esperados (opcional)' : 'Expected litres (optional)'}
          value={fuelForm.expectedLitres ?? ''}
          onChange={e => handle('expectedLitres', e.target.value)}
          type="number"
          inputProps={{ min: 0, max: 100000, step: 100 }}
          helperText={pt ? 'Auto = capacidade × 1000 (nominal)' : 'Auto = capacity × 1000 (nominal)'}
        />
      </Box>

      {/* Comboio — operador escolhe caso a caso entre "vínculo visual" e
          "OT partilhada" (P1 confirmada com Move Express, 2026-06-24). */}
      <Box style={{
        padding: '10px 12px', borderRadius: 8,
        background: '#fff', border: '1px solid #fed7aa',
      }}>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Box>
            <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#9a3412' }}>
              {pt ? '🚛🚛 Esta OT faz parte de um pedido composto?' : '🚛🚛 Is this OT part of a composite request?'}
            </Typography>
            <Typography className={classes.helpText}>
              {pt
                ? 'Composto = 2+ cisternas a satisfazer um slot maior (ex.: 35 + 25 = 60).'
                : 'Composite = 2+ tankers fulfilling a larger slot (e.g. 35 + 25 = 60).'}
            </Typography>
          </Box>
          <Switch
            checked={!!fuelForm.isConvoy}
            onChange={e => handle('isConvoy', e.target.checked)}
            color="primary"
          />
        </Box>

        {fuelForm.isConvoy && (
          <Box style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <TextField
              size="small" variant="outlined"
              label={pt ? 'OTs ligadas (IDs separados por vírgula)' : 'Linked OT IDs (comma-separated)'}
              value={(fuelForm.linkedToOtIds || []).join(', ')}
              onChange={e => {
                const ids = String(e.target.value)
                  .split(',').map(s => parseInt(s.trim())).filter(Number.isFinite);
                handle('linkedToOtIds', ids);
              }}
              helperText={pt
                ? 'Ex.: 4523, 4524. Podes preencher depois de criar as outras OTs.'
                : 'e.g. 4523, 4524. You can fill this after creating the other OTs.'}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!fuelForm.coordinatedDelivery}
                  onChange={e => handle('coordinatedDelivery', e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography style={{ fontSize: '0.74rem', color: '#0f172a' }}>
                  {pt
                    ? 'Entrega partilhada (mesma OT na app dos motoristas) — desligado = OTs separadas com vínculo visual'
                    : 'Shared delivery (same OT in driver apps) — off = separate OTs with visual link'}
                </Typography>
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default FuelOrderSection;
